import React, { useState, useEffect, useRef } from 'react';
import { OfflineChapter, getChapter } from '../services/mangaOfflineDB';
import { getReadingProgress, saveReadingProgress } from '../services/readingProgress';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  List,
  ArrowUp,
  BookOpen,
  Loader2,
  CheckCircle,
} from 'lucide-react';

interface MangaReaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  mangaId: string;
  mangaTitle: string;
  initialChapterNum: number;
  availableChapterNums: number[];
  onChapterChange?: (newChapterNum: number) => void;
  onMarkRead?: (chapterNum: number) => void;
}

export const MangaReaderModal: React.FC<MangaReaderModalProps> = ({
  isOpen,
  onClose,
  mangaId,
  mangaTitle,
  initialChapterNum,
  availableChapterNums,
  onChapterChange,
  onMarkRead,
}) => {
  const [currentChapterNum, setCurrentChapterNum] = useState<number>(initialChapterNum);
  const [chapterData, setChapterData] = useState<OfflineChapter | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMarkedRead, setHasMarkedRead] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bookmark: where to jump back to once this chapter's images have laid out
  const restoreTargetRef = useRef<{ pageIndex: number; pageOffset: number } | null>(null);
  const loadedImagesRef = useRef(0);
  const lastSaveRef = useRef(0);

  // Sync initialChapterNum
  useEffect(() => {
    if (isOpen) {
      setCurrentChapterNum(initialChapterNum);
    }
  }, [isOpen, initialChapterNum]);

  // Load chapter blobs from IndexedDB
  useEffect(() => {
    if (!isOpen || !mangaId || !currentChapterNum) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setHasMarkedRead(false);
    setCurrentPage(1);
    loadedImagesRef.current = 0;
    imageRefs.current = [];
    const saved = getReadingProgress(mangaId);
    restoreTargetRef.current =
      saved && saved.chapterNum === currentChapterNum && !saved.finished && (saved.pageIndex > 0 || saved.pageOffset > 0)
        ? { pageIndex: saved.pageIndex, pageOffset: saved.pageOffset }
        : null;

    // Revoke previous URLs
    imageUrls.forEach((url) => URL.revokeObjectURL(url));
    setImageUrls([]);

    getChapter(mangaId, currentChapterNum)
      .then((data) => {
        if (!isMounted) return;
        if (!data || !data.images || data.images.length === 0) {
          setError(`ไม่พบข้อมูลตอนที่ ${currentChapterNum} ในเครื่อง`);
          setIsLoading(false);
          return;
        }

        setChapterData(data);
        const urls = data.images.map((blob) => {
          if (blob.size > 0) {
            return URL.createObjectURL(blob);
          }
          return ''; // empty fallback
        });
        setImageUrls(urls);
        setIsLoading(false);

        // Reset scroll position to top
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || 'เกิดข้อผิดพลาดในการโหลดรูปภาพ');
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, mangaId, currentChapterNum]);

  // Clean up object URLs on modal close
  useEffect(() => {
    return () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Jump back to the saved spot; runs once every image has a height (or after a short wait)
  const restorePosition = () => {
    const target = restoreTargetRef.current;
    const container = containerRef.current;
    if (!target || !container) return;
    restoreTargetRef.current = null;
    const el = imageRefs.current[Math.min(target.pageIndex, imageRefs.current.length - 1)];
    if (!el) return;
    const containerTop = container.getBoundingClientRect().top;
    const rect = el.getBoundingClientRect();
    container.scrollTop =
      container.scrollTop + (rect.top - containerTop) + target.pageOffset * rect.height - container.clientHeight / 2;
    // Refresh the page counter now; a programmatic scroll doesn't always fire onScroll
    handleScroll();
  };

  const handleImageSettled = () => {
    loadedImagesRef.current += 1;
    if (loadedImagesRef.current >= imageUrls.filter(Boolean).length) restorePosition();
  };

  useEffect(() => {
    if (isLoading || imageUrls.length === 0 || !restoreTargetRef.current) return;
    const t = setTimeout(restorePosition, 2500);
    return () => clearTimeout(t);
  }, [isLoading, imageUrls]);

  // Track scroll position to update current page, save the bookmark and auto mark read at bottom
  const handleScroll = () => {
    if (!containerRef.current || imageUrls.length === 0) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    // Detect the image at the middle of the screen and how far into it we are
    const containerTop = containerRef.current.getBoundingClientRect().top;
    const middle = containerTop + clientHeight / 2;
    let visiblePage = 1;
    let pageOffset = 0;
    for (let i = 0; i < imageRefs.current.length; i++) {
      const el = imageRefs.current[i];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= middle) {
          visiblePage = i + 1;
          pageOffset = rect.height > 0 ? Math.min(1, Math.max(0, (middle - rect.top) / rect.height)) : 0;
        }
      }
    }
    setCurrentPage(visiblePage);

    const reachedEnd = scrollTop + clientHeight >= scrollHeight - 300;

    // Don't overwrite the bookmark while we're still waiting to jump back to it
    const now = Date.now();
    if (!restoreTargetRef.current && (reachedEnd || now - lastSaveRef.current > 400)) {
      lastSaveRef.current = now;
      saveReadingProgress(mangaId, {
        chapterNum: currentChapterNum,
        pageIndex: visiblePage - 1,
        pageOffset,
        totalPages: imageUrls.length,
        finished: reachedEnd || hasMarkedRead,
      });
    }

    // Auto mark read if scrolled near bottom (90%)
    if (!hasMarkedRead && reachedEnd) {
      setHasMarkedRead(true);
      onMarkRead?.(currentChapterNum);
    }
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const handlePrevChapter = () => {
    const sorted = [...availableChapterNums].sort((a, b) => a - b);
    const idx = sorted.indexOf(currentChapterNum);
    if (idx > 0) {
      const prev = sorted[idx - 1];
      setCurrentChapterNum(prev);
      onChapterChange?.(prev);
    }
  };

  const handleNextChapter = () => {
    const sorted = [...availableChapterNums].sort((a, b) => a - b);
    const idx = sorted.indexOf(currentChapterNum);
    if (idx !== -1 && idx < sorted.length - 1) {
      const next = sorted[idx + 1];
      setCurrentChapterNum(next);
      onChapterChange?.(next);
    }
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!isOpen) return null;

  const sortedChapters = [...availableChapterNums].sort((a, b) => a - b);
  const currentChapterIdx = sortedChapters.indexOf(currentChapterNum);
  const hasPrev = currentChapterIdx > 0;
  const hasNext = currentChapterIdx !== -1 && currentChapterIdx < sortedChapters.length - 1;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col select-none overflow-hidden">
      <div aria-hidden className="reader-status-strip fixed top-0 left-0 right-0 z-[51] bg-black pointer-events-none" />
      {/* Top Floating Control Bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-canvas/85 backdrop-blur-xl border-b border-white/[0.06] px-3 sm:px-6 flex items-center justify-between transition-transform duration-200 reader-top-bar ${
          showControls ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            title="ปิดหน้าอ่าน"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h3 className="text-xs sm:text-sm font-bold text-white truncate max-w-[200px] sm:max-w-md">
              {mangaTitle}
            </h3>
            <div className="flex items-center gap-2 text-[11px] text-zinc-400">
              <span>ตอนที่ {currentChapterNum}</span>
              {chapterData?.sourceName && (
                <span className="text-zinc-500">• {chapterData.sourceName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Chapter Selector Dropdown */}
          <select
            value={currentChapterNum}
            onChange={(e) => {
              const num = Number(e.target.value);
              setCurrentChapterNum(num);
              onChapterChange?.(num);
            }}
            className="bg-zinc-900 ring-1 ring-inset ring-white/10 text-zinc-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
          >
            {sortedChapters.map((num) => (
              <option key={num} value={num}>
                ตอนที่ {num}
              </option>
            ))}
          </select>

          {/* Fullscreen Toggle */}
          <button
            onClick={handleToggleFullscreen}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors hidden sm:block"
            title={isFullscreen ? 'ออกจากเต็มจอ' : 'เต็มจอ'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main Image Continuous Scroll View */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        onClick={() => setShowControls((prev) => !prev)}
        className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col items-center bg-neutral-950 overscroll-contain reader-scroll-view"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {isLoading && (
          <div className="flex-1 flex flex-col items-center justify-center py-32 text-zinc-400">
            <Loader2 className="w-8 h-8 animate-spin mb-3" />
            <span className="text-sm font-medium">กำลังเตรียมภาพตอนที่ {currentChapterNum}...</span>
          </div>
        )}

        {error && (
          <div className="flex-1 flex flex-col items-center justify-center py-32 text-rose-400 px-4 text-center">
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs"
            >
              ปิดหน้าอ่าน
            </button>
          </div>
        )}

        {!isLoading && !error && imageUrls.length > 0 && (
          <div className="w-full max-w-3xl flex flex-col items-center shadow-2xl">
            {imageUrls.map((url, idx) =>
              url ? (
                <img
                  key={idx}
                  ref={(el) => (imageRefs.current[idx] = el)}
                  src={url}
                  alt={`หน้า ${idx + 1}`}
                  onLoad={handleImageSettled}
                  onError={handleImageSettled}
                  className="w-full block select-none pointer-events-none"
                  style={{ display: 'block' }}
                />
              ) : (
                <div
                  key={idx}
                  className="w-full h-48 bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 text-xs"
                >
                  หน้า {idx + 1} โหลดไม่สมบูรณ์
                </div>
              )
            )}

            {/* End of Chapter Card */}
            <div className="w-full py-12 px-4 flex flex-col items-center justify-center bg-zinc-900/50 border-t border-zinc-800 text-center space-y-4">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                <CheckCircle className="w-5 h-5" />
                <span>อ่านจบตอนที่ {currentChapterNum} เรียบร้อยแล้ว!</span>
              </div>

              <div className="flex items-center gap-3">
                {hasPrev && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevChapter();
                    }}
                    className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>ตอนก่อนหน้า</span>
                  </button>
                )}

                {hasNext ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextChapter();
                    }}
                    className="px-5 py-2 bg-zinc-50 hover:bg-white text-zinc-900 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all active:scale-95"
                  >
                    <span>อ่านตอนถัดไป</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <span className="text-xs text-zinc-500">นี่คือตอนล่าสุดที่คุณดาวน์โหลดไว้แล้ว</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Floating Navigation Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-canvas/85 backdrop-blur-xl border-t border-white/[0.06] px-4 flex items-center justify-between transition-transform duration-200 reader-bottom-bar ${
          showControls ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Prev Chapter */}
        <button
          onClick={handlePrevChapter}
          disabled={!hasPrev}
          className="p-1.5 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-lg disabled:opacity-30 disabled:pointer-events-none transition-colors flex items-center gap-1 text-xs"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">ตอนก่อน</span>
        </button>

        {/* Page / Progress indicator */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-400 font-medium">
            หน้า {currentPage} / {imageUrls.length || 0}
          </span>
          <button
            onClick={scrollToTop}
            title="เลื่อนขึ้นบนสุด"
            className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-800 transition-colors"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Next Chapter */}
        <button
          onClick={handleNextChapter}
          disabled={!hasNext}
          className="p-1.5 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-lg disabled:opacity-30 disabled:pointer-events-none transition-colors flex items-center gap-1 text-xs"
        >
          <span className="hidden sm:inline">ตอนถัดไป</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
